/**
 * useNotificationRealtime Hook
 * Subscribes to real-time notifications from Supabase
 * Shows celebration modal when new notifications arrive
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { InAppNotification } from "../types";

interface UseNotificationRealtimeOptions {
  /** Whether to auto-show celebration modal for new notifications */
  autoShowCelebration?: boolean;
  /** Types of notifications to listen to */
  filterTypes?: string[];
}

interface UseNotificationRealtimeReturn {
  /** Latest notification received */
  currentNotification: InAppNotification | null;
  /** Whether celebration modal should be visible */
  showCelebration: boolean;
  /** Dismiss the celebration modal */
  dismissCelebration: () => void;
  /** Whether carousel should be visible (for existing unread notifications on app open) */
  showCarousel: boolean;
  /** Dismiss the carousel */
  dismissCarousel: () => void;
  /** List of unread notifications */
  unreadNotifications: InAppNotification[];
  /** Mark notification as read */
  markAsRead: (notificationId: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Total unread count */
  unreadCount: number;
  /** Whether we're loading */
  isLoading: boolean;
  /** Any error */
  error: string | null;
}

/**
 * Hook to subscribe to real-time notifications
 */
export const useNotificationRealtime = (
  options: UseNotificationRealtimeOptions = {}
): UseNotificationRealtimeReturn => {
  const { autoShowCelebration = true, filterTypes } = options;
  const { user, profile } = useAuth();
  
  const [currentNotification, setCurrentNotification] = useState<InAppNotification | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState<InAppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track processed notification IDs to avoid duplicates
  const processedIds = useRef<Set<string>>(new Set());
  // Track if we've shown the initial carousel
  const hasShownInitialCarousel = useRef(false);

  /**
   * Fetch unread notifications on mount
   */
  const fetchUnreadNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      let query = supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_read", false)
        .order("created_at", { ascending: false });
      
      if (filterTypes && filterTypes.length > 0) {
        query = query.in("notification_type", filterTypes);
      }
      
      const { data, error: fetchError } = await query;
      
      if (fetchError) {
        console.error("Error fetching notifications:", fetchError);
        setError(fetchError.message);
        return;
      }
      
      setUnreadNotifications(data || []);
      
      // Add to processed IDs
      data?.forEach(n => processedIds.current.add(n.id));
      
      // Show carousel if there are unread notifications on initial load
      if (data && data.length > 0 && !hasShownInitialCarousel.current) {
        hasShownInitialCarousel.current = true;
        setShowCarousel(true);
      }
      
    } catch (err) {
      console.error("Error in fetchUnreadNotifications:", err);
      setError("Kon notificaties niet ophalen");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filterTypes]);

  /**
   * Handle new notification from realtime
   */
  const handleNewNotification = useCallback((notification: InAppNotification) => {
    // Skip if already processed
    if (processedIds.current.has(notification.id)) return;
    
    // Check type filter
    if (filterTypes && filterTypes.length > 0) {
      if (!filterTypes.includes(notification.notification_type || "")) {
        return;
      }
    }
    
    processedIds.current.add(notification.id);
    
    // Add to unread list
    setUnreadNotifications(prev => [notification, ...prev]);
    
    // Show celebration if enabled
    if (autoShowCelebration) {
      setCurrentNotification(notification);
      setShowCelebration(true);
    }
  }, [autoShowCelebration, filterTypes]);

  /**
   * Subscribe to realtime notifications
   */
  useEffect(() => {
    if (!user?.id) return;
    
    // Initial fetch
    fetchUnreadNotifications();
    
    // Subscribe to INSERT events on notifications table
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const notification = payload.new as InAppNotification;
          handleNewNotification(notification);
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchUnreadNotifications, handleNewNotification]);

  /**
   * Dismiss celebration modal
   */
  const dismissCelebration = useCallback(() => {
    setShowCelebration(false);
    
    // Mark as read after dismissing
    if (currentNotification) {
      markAsReadInternal(currentNotification.id);
      setCurrentNotification(null);
    }
  }, [currentNotification]);

  /**
   * Dismiss carousel
   */
  const dismissCarousel = useCallback(() => {
    setShowCarousel(false);
  }, []);

  /**
   * Mark notification as read (internal)
   */
  const markAsReadInternal = async (notificationId: string) => {
    try {
      await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("id", notificationId);
      
      setUnreadNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  /**
   * Mark notification as read (public)
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    await markAsReadInternal(notificationId);
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!user?.id || unreadNotifications.length === 0) return;
    
    try {
      await supabase
        .from("notifications")
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq("user_id", user.id)
        .eq("is_read", false);
      
      setUnreadNotifications([]);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  }, [user?.id, unreadNotifications.length]);

  return {
    currentNotification,
    showCelebration,
    dismissCelebration,
    showCarousel,
    dismissCarousel,
    unreadNotifications,
    markAsRead,
    markAllAsRead,
    unreadCount: unreadNotifications.length,
    isLoading,
    error,
  };
};

export default useNotificationRealtime;


